

// src/pages/Calculator.js
import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { fetchVehicleData } from '../services/synsbasenApi';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth';

// Samme designsprog som ab-admin (Autobasen): hvid baggrund med fritstående
// flader i #F7F8F7, bløde skygger, 12–16px hjørner og grøn accent #00B861.
const SURFACE = '#F7F8F7';
const SURFACE_SHADOW = '0 2px 8px rgba(16, 24, 40, 0.06)';
const BORDER = '#E5E5E5';
const MUTED = '#6B7280';
const GREEN = '#00B861';

const Container = styled.div`
  display: flex;
  gap: 12px;
  height: 100vh;
  padding: 12px;
  background-color: #ffffff;
`;

const LeftPanel = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  background-color: ${SURFACE};
  border-radius: 16px;
  box-shadow: ${SURFACE_SHADOW};
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
`;

const RightTopPanel = styled.div`
  flex: 0 0 30%; /* Maksimalt 30% af højden */
  padding: 20px 24px;
  overflow-y: auto;
  background-color: ${SURFACE};
  border-radius: 16px;
  box-shadow: ${SURFACE_SHADOW};
`;

const RightBottomPanel = styled.div`
  flex: 1; /* Tager resten af pladsen */
  padding: 20px 24px;
  overflow-y: auto;
  background-color: ${SURFACE};
  border-radius: 16px;
  box-shadow: ${SURFACE_SHADOW};
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
`;

const Logo = styled.div`
  img {
    height: 32px;
    display: block;
  }
`;

const Title = styled.h1`
  color: #171717;
  font-size: 18px;
  font-weight: 700;
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  background: #ffffff;
  color: #000000;
  font-size: 14px;
  transition: box-shadow 0.2s, border-color 0.2s;

  &::placeholder {
    color: #A3A3A3;
  }

  &:focus {
    outline: none;
    border-color: ${GREEN};
    box-shadow: 0 0 0 2px ${GREEN};
  }
`;

const Button = styled.button`
  background: ${GREEN};
  color: white;
  border: none;
  min-height: 46px;
  padding: 0 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #008f4c;
  }
`;

const VehicleOverview = styled.div`
  background-color: transparent;
  padding: 0;
  box-shadow: none;
`;

const OverviewTitle = styled.h2`
  color: #171717;
  font-size: 16px;
  font-weight: 700;
  margin-top: 0;
  margin-bottom: 12px;
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 24px;
  row-gap: 0;
`;

const OverviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid #ECEEEC;
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
`;

const OverviewLabel = styled.span`
  color: ${MUTED};
`;

const OverviewValue = styled.span`
  font-weight: 600;
  color: #171717;
  text-align: right;
`;

const ResultSection = styled.div`
  margin-top: 24px;
  margin-bottom: 8px;
  background: #ffffff;
  border: 1px solid ${BORDER};
  border-radius: 16px;
  padding: 20px;
`;

const ResultTitle = styled.h2`
  color: #171717;
  font-size: 16px;
  font-weight: 700;
  margin-top: 0;
  margin-bottom: 8px;
`;

const ResultRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #F0F1F0;
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }
`;

const ResultLabel = styled.span`
  color: ${MUTED};
`;

const ResultValue = styled.span`
  font-weight: 600;
  color: #171717;
  font-variant-numeric: tabular-nums;
`;

const HighlightedValue = styled(ResultValue)`
  color: ${GREEN};
  font-size: 18px;
  font-weight: 700;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Spinner = styled.div`
  border: 3px solid #E5E7EB;
  border-top: 3px solid ${GREEN};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Hjælpetekst under felter
const helperStyle = { color: MUTED, display: 'block', marginTop: '6px', fontSize: '12px' };

function Calculator() {


  const [registrationNumber, setRegistrationNumber] = useState('');
  const [handelspris, setHandelspris] = useState('');
  const [normKm, setNormKm] = useState('');
  const [currentKm, setCurrentKm] = useState('');
  const [eurPrice, setEurPrice] = useState('');
  const [vehicleData, setVehicleData] = useState(null);
  const [results, setResults] = useState(null);
  const [euroResults, setEuroResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [manualNewPrice, setManualNewPrice] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
const [warningMessage, setWarningMessage] = useState('');
  
  const registrationNumberRef = useRef(null);
  const handelsprisRef = useRef(null);
  const manualNewPriceRef = useRef(null);
  const normKmRef = useRef(null);
  const currentKmRef = useRef(null);
  const eurPriceRef = useRef(null);

  // Brugeren (og dermed regnearket) bestemmes af login
  const { user, logout } = useAuth();
  const selectedUser = user.initials;

const handleContinueWithWarning = () => {
  setShowWarningModal(false);
  // Fortsæt med beregningen
};
const handleResetAfterWarning = () => {
  setShowWarningModal(false);
  // Nulstil formularen
  setRegistrationNumber('');
  setHandelspris('');
  setNormKm('');
  setCurrentKm('');
  setEurPrice('');
  setVehicleData(null);
  setResults(null);
  setEuroResults(null);
  
  // Fokuser på nummerplade-feltet igen
  setTimeout(() => {
    if (registrationNumberRef.current) {
      registrationNumberRef.current.focus(); 
    }
  }, 100);
};

  const handleRegistrationNumberSearch = async (e) => {
    e.preventDefault();
    if (!registrationNumber) {
      setError('Indtast venligst et registreringsnummer');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await fetchVehicleData(registrationNumber);
      setVehicleData(data);

      // Tjek om vurderingsdata mangler, bilen er leaset eller mangler registreringsafgift
if (data.dmrUnavailable) {
  const leasingNote = data.leasingPeriodEnd && data.leasingPeriodEnd !== "Nej" ? ` Bemærk også: bilen er leaset indtil ${data.leasingPeriodEnd}.` : '';
  setWarningMessage(`Vurderingsdata (vurdering, registreringsafgift og nypris) kan ikke hentes fra Synsbasen lige nu. Prøv igen senere, eller fortsæt og indtast nyprisen manuelt.${leasingNote}`);
  setShowWarningModal(true);
} else if (data.leasingPeriodEnd && data.leasingPeriodEnd !== "Nej") {
  setWarningMessage(`Bemærk: Denne bil er leaset indtil ${data.leasingPeriodEnd}. Vil du fortsætte?`);
  setShowWarningModal(true);
} else if (data.registrationTax === null || data.registrationTax === undefined) {
  setWarningMessage('Bemærk: Denne bil har ingen registreringsafgift angivet. Vil du fortsætte?');
  setShowWarningModal(true);
}

   
// Hent kilometertal fra HubSpot
 // Hent kilometertal fra HubSpot
 try {
  const hubspotResponse = await axios.get(`/api/hubspot/mileage/${registrationNumber}`);
  const hubspotData = hubspotResponse.data;
  if (hubspotData) {
    if (hubspotData.kilometer) {
      setCurrentKm(hubspotData.kilometer);
      console.log(`Kilometertal hentet fra HubSpot: ${hubspotData.kilometer}`);
    }
    
    // Gem dealId i vehicleData
    if (hubspotData.deal_id) {
      setVehicleData(prevData => ({
        ...prevData,
        dealId: hubspotData.deal_id
      }));
      console.log(`Deal ID gemt: ${hubspotData.deal_id}`);
    }
  }
} catch (hubspotError) {
  console.error('Fejl ved hentning af HubSpot data:', hubspotError);
}
      
      // Nulstil beregningsresultater når en ny bil hentes
      setResults(null);
      setEuroResults(null);
      
      // Automatisk fokus på handelspris-feltet
      setTimeout(() => {
        if (handelsprisRef.current) {
          handelsprisRef.current.focus();
        }
      }, 100);
    } catch (err) {
      setError(err.message || 'Der opstod en fejl ved hentning af køretøjsdata');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateResults = async (e) => {
    e.preventDefault();
    
    if (!vehicleData) {
      setError('Hent venligst køretøjsdata først');
      return;
    }
    
    if (!handelspris) {
      setError('Udfyld venligst handelspris');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Trin 1: Kald backend for at beregne handelspris baseret på alder og km
      const tradePriceResponse = await axios.post('/api/calculate-trade-price', {
        vehicleAge: vehicleData.vehicleAge,
        handelspris: parseFloat(handelspris),
        normKm: parseFloat(normKm || 0),
        currentKm: parseFloat(currentKm || 0),
        selectedUser
      });
      
      const { sheetHandelspris, ageGroup } = tradePriceResponse.data;
      
      // Beregn nypris
      const newPrice = manualNewPrice !== '' ? parseFloat(manualNewPrice) : (vehicleData?.newPrice ? parseFloat(vehicleData.newPrice) : null);

      
      // Trin 2: Opdater Google Sheets med handelspris og nypris baseret på biltype
      await axios.post('/api/update-vehicle-data', {
        vehicleType: vehicleData.vehicleType,
        totalWeight: vehicleData.totalWeight || 0,
        handelspris: sheetHandelspris, // Brug handelspris fra sheet
        newPrice: newPrice,
        selectedUser
      });
      
      // Trin 3: Beregn CO2-værdi
      const co2Response = await axios.post('/api/calculate-co2', {
        fuelType: vehicleData.fuelType,
        fuelEfficiency: vehicleData.fuelEfficiency,
        vehicleType: vehicleData.vehicleType,
        registrationNumber: registrationNumber,
        selectedUser
      });
      
      const co2Value = co2Response.data.co2Value;
      
      // Trin 4: Hent eksportafgift fra Google Sheets (beregn automatisk baseret på inputs)
      const taxResponse = await axios.post('/api/calculate-export-tax', {
        vehicleType: vehicleData.vehicleType,
        exportRefundCeiling: vehicleData.exportRefundCeiling,
        selectedUser
      });
      
      const { exportTax, reducedTax } = taxResponse.data;
      
      setResults({
        vehicleType: vehicleData.vehicleType,
        vehicleInfo: `${vehicleData.brand} ${vehicleData.model} ${vehicleData.variant}`,
        vehicleAge: vehicleData.vehicleAge,
        ageGroup: ageGroup,
        sheetHandelspris: sheetHandelspris,
        newPrice: newPrice,
        exportTax: exportTax,
        reducedTax: reducedTax,
        co2Value: co2Value,
      });
      
      // Automatisk fokus på Euro-feltet
      setTimeout(() => {
        if (eurPriceRef.current) {
          eurPriceRef.current.focus();
        }
      }, 100);
      
    } catch (err) {
      setError(err.response?.data?.error || 'Der opstod en fejl under beregningen');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleEuroCalculation = async (e) => {
    e.preventDefault();
    
    if (!results || !eurPrice) {
      setError('Udfyld venligst Euro pris');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Kald backend for at beregne Euro-værdier
      const response = await axios.post('/api/calculate-euro', {
        eurPrice: parseFloat(eurPrice),
        reducedTax: results.reducedTax
      });
      
      setEuroResults(response.data);
      
    } catch (err) {
      setError('Der opstod en fejl under Euro-beregningen');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSubmitToHubspot = async () => {
    if (!vehicleData || !results || !euroResults) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Kald backend for at sende data til HubSpot og logge til Google Sheets
      await axios.post('/api/submit', {
        registrationNumber,
        vehicleType: vehicleData.vehicleType,
        vehicleInfo: `${vehicleData.brand} ${vehicleData.model} ${vehicleData.variant}`,
        newPrice: results.newPrice,
        exportTax: results.exportTax,
        reducedTax: results.reducedTax,
        handelsprisInput: parseFloat(handelspris),
        normKmInput: parseFloat(normKm),
        currentKmInput: parseFloat(currentKm),
        sheetHandelspris: results.sheetHandelspris,
        ageGroup: results.ageGroup,
        eurPrice: parseFloat(eurPrice),
        dkkConverted: euroResults.dkkConverted,
        totalSum: euroResults.totalSum,
        dealId: vehicleData.dealId,  // Tilføj dealId fra vehicleData
        selectedUser: selectedUser // Tilføj denne linje
      });
      
      // Nulstil formularen
      setRegistrationNumber('');
      setHandelspris('');
      setNormKm('');
      setCurrentKm('');
      setEurPrice('');
      setVehicleData(null);
      setResults(null);
      setEuroResults(null);
      
      // Fokuser på nummerplade-feltet igen
      setTimeout(() => {
        if (registrationNumberRef.current) {
          registrationNumberRef.current.focus(); 
        }
      }, 100);
      
      // Vis bekræftelse til brugeren
      alert('Data er sendt til HubSpot og logget i Google Sheets!');
      
    } catch (err) {
      setError('Der opstod en fejl ved afsendelse til HubSpot');
    } finally {
      setIsLoading(false);
    }
  };

  // Håndterer tastaturgenveje for hurtig navigation mellem felter
  const handleKeyDown = (e, nextFieldRef) => {
    if (e.key === 'Enter' && nextFieldRef) {
      e.preventDefault();
      nextFieldRef.current.focus();
    }
  };


  return (
    <>
{showWarningModal && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(17, 24, 39, 0.4)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2000
  }}>
    <div style={{
      backgroundColor: 'white',
      padding: '28px',
      borderRadius: '16px',
      width: '420px',
      boxShadow: '0 12px 32px rgba(16, 24, 40, 0.18)'
    }}>
      <h2 style={{ marginTop: 0, marginBottom: '16px', color: '#171717', fontSize: '18px', fontWeight: 700 }}>Advarsel</h2>
      <div style={{ 
        padding: '14px 16px',
        backgroundColor: '#FFFBEB',
        border: '1px solid #FDE68A',
        borderRadius: '12px',
        marginBottom: '20px',
        color: '#92400E',
        fontSize: '14px'
      }}>
        <p style={{ margin: 0 }}>{warningMessage}</p>
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
        <button 
          onClick={handleResetAfterWarning}
          style={{
            minHeight: '44px',
            padding: '0 18px',
            backgroundColor: 'white',
            color: '#171717',
            border: `1px solid ${BORDER}`,
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Nulstil beregner
        </button>
        <button 
          onClick={handleContinueWithWarning}
          style={{
            minHeight: '44px',
            padding: '0 18px',
            backgroundColor: GREEN,
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Fortsæt alligevel
        </button>
      </div>
    </div>
  </div>
)}

  
      <Container>
        {isLoading && (
          <LoadingOverlay>
            <Spinner />
          </LoadingOverlay>
        )}
        
        <LeftPanel>
          <Header>
            <Logo>
              <img src="/Autobasen_logo.svg" alt="Autobasen Logo" />
            </Logo>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <Title>ABBeregner</Title>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                color: MUTED,
                marginTop: '6px'
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '26px',
                  height: '26px',
                  borderRadius: '50%',
                  backgroundColor: GREEN,
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: 600
                }}>
                  {user.initials}
                </span>
                <span style={{ color: '#171717', fontWeight: 600 }}>{user.name}</span>
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    style={{ color: MUTED, fontSize: '12px', fontWeight: 500, textDecoration: 'underline' }}
                  >
                    Brugere
                  </Link>
                )}
                <button
                  onClick={logout}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: MUTED,
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 500,
                    textDecoration: 'underline'
                  }}
                >
                  Log ud
                </button>
              </div>
            </div>
          </Header>
  
          <Form onSubmit={handleRegistrationNumberSearch}>
            <FormGroup>
              <Label>Nummerplade</Label>
              <div style={{ display: 'flex' }}>
                <Input 
                  ref={registrationNumberRef}
                  type="text" 
                  value={registrationNumber}
                  onChange={(e) => setRegistrationNumber(e.target.value)}
                  placeholder="Indtast nummerplade"
                  style={{ marginRight: '8px' }}
                />
                <Button type="submit">Søg</Button>
              </div>
            </FormGroup>
          </Form>
          
          {error && (
            <div style={{
              color: '#B91C1C',
              backgroundColor: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: '12px',
              padding: '10px 14px',
              fontSize: '14px',
              marginTop: '4px'
            }}>
              {error}
            </div>
          )}
          
          {vehicleData && (
            <>
              <Form onSubmit={calculateResults} style={{ marginTop: '30px' }}>
                <FormGroup>
                  <Label>Handelspris (DKK)</Label>
                  <Input 
                    ref={handelsprisRef}
                    type="number" 
                    value={handelspris}
                    onChange={(e) => setHandelspris(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, normKmRef)}
                    placeholder="Indtast handelspris"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Nypris (DKK)</Label>
                  <Input
                    ref={manualNewPriceRef}
                    type="number"
                    value={manualNewPrice !== '' ? manualNewPrice : (vehicleData?.newPrice || '')}
                    onChange={e => setManualNewPrice(e.target.value)}
                    onKeyDown={e => handleKeyDown(e, normKmRef)}
                    placeholder="Indtast nypris"
                  />
                  {manualNewPrice !== '' ? (
                    <small style={helperStyle}>
                      Nypris er manuelt indtastet
                    </small>
                  ) : vehicleData?.newPriceSource === 'dmr' ? (
                    <small style={helperStyle}>
                      Nypris hentet fra DMR
                    </small>
                  ) : vehicleData?.newPriceSource === 'beregnet-dmr' ? (
                    <small style={helperStyle}>
                      Nypris beregnet fra DMR-data (vurdering + afgift)
                    </small>
                  ) : (
                    <small style={helperStyle}>
                      Nypris mangler - indtast venligst
                    </small>
                  )}
                </FormGroup>

                
                
                <FormGroup>
                  <Label>Norm kilometer</Label>
                  <Input 
                    ref={normKmRef}
                    type="number" 
                    value={normKm}
                    onChange={(e) => setNormKm(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, currentKmRef)}
                    placeholder="Indtast norm kilometer"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Aktuelle kilometer</Label>
                  <Input 
                    ref={currentKmRef}
                    type="number" 
                    value={currentKm}
                    onChange={(e) => setCurrentKm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && calculateResults(e)}
                    placeholder="Indtast aktuelle kilometer"
                  />
                  {vehicleData?.hubspotKilometer && (
                    <small style={helperStyle}>Kilometertal fra HubSpot: {vehicleData.hubspotKilometer}</small>
                  )}
                </FormGroup>
                
                <Button type="submit">Beregn</Button>
              </Form>
              
              {results && (
                <ResultSection>
                  <ResultTitle>Beregningsresultat</ResultTitle>
                  <ResultRow>
                    <ResultLabel>Handelspris fra sheet:</ResultLabel>
                    <ResultValue>{results.sheetHandelspris.toLocaleString('da-DK')} kr. ({results.ageGroup})</ResultValue>
                  </ResultRow>
                  <ResultRow>
                    <ResultLabel>Nypris:</ResultLabel>
                    <ResultValue>{results.newPrice.toLocaleString('da-DK')} kr.</ResultValue>
                  </ResultRow>
                  <ResultRow>
                    <ResultLabel>Eksportafgift:</ResultLabel>
                    <ResultValue>{results.exportTax.toLocaleString('da-DK')} kr.</ResultValue>
                  </ResultRow>
                  <ResultRow>
                    <ResultLabel>Eksportgodtgørelse (reduceret):</ResultLabel>
                    <ResultValue>{results.reducedTax.toLocaleString('da-DK', { maximumFractionDigits: 2})} kr.</ResultValue>
                  </ResultRow>
  
                  <div style={{ marginTop: '16px', borderTop: '1px solid #F0F1F0', paddingTop: '16px' }}>
                    <FormGroup>
                      <Label>Euro pris</Label>
                      <div style={{ display: 'flex' }}>
                        <Input 
                          ref={eurPriceRef}
                          type="number" 
                          value={eurPrice}
                          onChange={(e) => setEurPrice(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (euroResults) {
                                // Hvis euroResults allerede findes, send til HubSpot
                                handleSubmitToHubspot();
                              } else {
                                // Ellers beregn først
                                handleEuroCalculation(e);
                              }
                            }
                          }}
                          placeholder="Indtast Euro pris"
                          style={{ marginRight: '8px' }}
                        />
                        <Button onClick={handleEuroCalculation}>Beregn</Button>
                      </div>
                    </FormGroup>
                  </div>
                  
                  {euroResults && (
                    <div style={{ marginTop: '15px' }}>
                      <ResultRow>
                        <ResultLabel>Euro pris:</ResultLabel>
                        <ResultValue>{parseFloat(eurPrice).toLocaleString('da-DK')} EUR</ResultValue>
                      </ResultRow>
  
                      <ResultRow>
                        <ResultLabel>Samler:</ResultLabel>
                        <HighlightedValue>{euroResults.totalSum.toLocaleString('da-DK', { maximumFractionDigits: 2})} kr.</HighlightedValue>
                      </ResultRow>
                      <div style={{ marginTop: '16px', textAlign: 'right' }}>
                        <Button onClick={handleSubmitToHubspot}>Send</Button>
                      </div>
                    </div>
                  )}
                </ResultSection>
              )}
            </>
          )}
        </LeftPanel>
        
        <RightPanel>
          <RightTopPanel>
            {vehicleData ? (
              <VehicleOverview>
                <OverviewTitle>{vehicleData.brandModel} {vehicleData.variant}</OverviewTitle>
                <OverviewGrid>
                  <div>
                    <OverviewRow>
                      <OverviewLabel>Karosseri:</OverviewLabel>
                      <OverviewValue>{vehicleData.bodyType}</OverviewValue>
                    </OverviewRow>
                    <OverviewRow>
                      <OverviewLabel>Type:</OverviewLabel>
                      <OverviewValue>{vehicleData.kind} {vehicleData.usage}</OverviewValue>
                    </OverviewRow>
            <OverviewRow>
                    <OverviewLabel>Totalvægt:</OverviewLabel>
                    <OverviewValue>
                      {vehicleData.totalWeight ? `${vehicleData.totalWeight.toLocaleString('da-DK')} kg` : 'N/A'}
                    </OverviewValue>
                    </OverviewRow>
                    <OverviewRow>
                      <OverviewLabel>Alder:</OverviewLabel>
                      <OverviewValue>{vehicleData.vehicleAge} år</OverviewValue>
                    </OverviewRow>
                    <OverviewRow>
                      <OverviewLabel>Første reg.:</OverviewLabel>
                      <OverviewValue>{vehicleData.firstRegistrationDate}</OverviewValue>
                    </OverviewRow>
                    <OverviewRow>
                      <OverviewLabel>Leaset?:</OverviewLabel>
                      <OverviewValue>{vehicleData.leasingPeriodEnd}</OverviewValue>
                    </OverviewRow>
                  </div>
                  <div>
                    <OverviewRow>
                      <OverviewLabel>Brændstof:</OverviewLabel>
                      <OverviewValue>{vehicleData.fuelType}</OverviewValue>
                    </OverviewRow>
                    <OverviewRow>
                      <OverviewLabel>Motor:</OverviewLabel>
                      <OverviewValue>{vehicleData.horsepower} HK</OverviewValue>
                    </OverviewRow>
                    <OverviewRow>
                      <OverviewLabel>Slagvolumen:</OverviewLabel>
                      <OverviewValue>{vehicleData.engineDisplacement} ccm</OverviewValue>
                    </OverviewRow>
                    <OverviewRow>
                      <OverviewLabel>Reg. afgift:</OverviewLabel>
                      <OverviewValue>{vehicleData.registrationTax ? vehicleData.registrationTax.toLocaleString('da-DK') + ' kr.' : 'N/A'}</OverviewValue>
                    </OverviewRow>
                    <OverviewRow>
                      <OverviewLabel>Eksportloft:</OverviewLabel>
                      <OverviewValue>{vehicleData.exportRefundCeiling ? vehicleData.exportRefundCeiling.toLocaleString('da-DK') + ' kr.' : 'N/A'}</OverviewValue>
                    </OverviewRow>
                    <OverviewRow>
                      <OverviewLabel>Nypris:</OverviewLabel>
                      <OverviewValue>{vehicleData.newPrice ? vehicleData.newPrice.toLocaleString('da-DK') + ' kr.' : 'N/A'}</OverviewValue>
                    </OverviewRow>
                    <OverviewRow>
                      <OverviewLabel>Sidste syn:</OverviewLabel>
                      <OverviewValue>{vehicleData.lastInspectionDate}</OverviewValue>
                    </OverviewRow>
                  </div>
                </OverviewGrid>
              </VehicleOverview>
            ) : (
              <div style={{ textAlign: 'center', margin: 'auto', color: MUTED, padding: '12px 0' }}>
                <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#171717', marginBottom: '6px' }}>Køretøjsoverblik</h2>
                <p style={{ fontSize: '14px' }}>Vælg en bil ved at søge på nummerpladen i venstre side.</p>
              </div>
            )}
          </RightTopPanel>
          
          <RightBottomPanel>
            <div style={{ textAlign: 'center', color: MUTED, padding: '12px 0' }}>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#171717', marginBottom: '6px' }}>Yderligere oplysninger</h2>
              <p style={{ fontSize: '14px' }}>Her kan du vise yderligere information eller funktionalitet.</p>
            </div>
          </RightBottomPanel>
        </RightPanel>
      </Container>
    </>
  );
  
}

export default Calculator;
